import { createFileRoute, isRedirect } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@beweegbaas/ui";
import { m } from "#/paraglide/messages";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { LayoutAuth } from "#/layouts/layout.auth";
import {
  login2FACodeFormSchema,
  login2FARecoveryFormSchema,
} from "#/modules/generic.login/schema.2fa";
import { redirectIfAuthenticated, verify2FALoginFn } from "#/modules/util.auth/auth.functions";
import { loginWith2FARecovery } from "#/modules/util.auth/twofa.functions";
import { useAppForm } from "#/modules/util.forms/form";
import { z } from "zod/v4";

export const Route = createFileRoute("/$locale/{-$schoolyear}/login-2fa")({
  validateSearch: z.object({
    email: z.string().catch(""),
  }),
  // Back after a finished login lands here, where no code can succeed any more: send the user on,
  // like /login does, and likewise never during a preload (see login.tsx).
  beforeLoad: async ({ context, preload }) => {
    if (context.user && !preload) {
      await redirectIfAuthenticated();
    }
  },
  component: Login2FAPage,
});

function Login2FAPage() {
  const { email } = Route.useSearch();
  const verify = useServerFn(verify2FALoginFn);
  const recover = useServerFn(loginWith2FARecovery);
  const [useRecovery, setUseRecovery] = useState(false);

  const codeForm = useAppForm({
    defaultValues: { code: "" },
    validators: { onSubmit: login2FACodeFormSchema },
    onSubmit: async ({ value }) => {
      try {
        const res = await verify({ data: { email, code: value.code } });
        if (res) codeForm.setErrorMap({ onSubmit: { form: res.formError, fields: {} } });
      } catch (error) {
        if (isRedirect(error)) throw error;
        captureUnexpectedError(error, "login-2fa");
        codeForm.setErrorMap({
          onSubmit: { form: m["common.something_went_wrong"](), fields: {} },
        });
      }
    },
  });

  const recoveryForm = useAppForm({
    defaultValues: { recoveryCode: "" },
    validators: { onSubmit: login2FARecoveryFormSchema },
    onSubmit: async ({ value }) => {
      try {
        const res = await recover({ data: { email, recoveryCode: value.recoveryCode } });
        if (res) {
          recoveryForm.setErrorMap({ onSubmit: { form: res.formError, fields: {} } });
        }
      } catch (error) {
        if (isRedirect(error)) throw error;
        captureUnexpectedError(error, "login-2fa-recovery");
        recoveryForm.setErrorMap({
          onSubmit: { form: m["common.something_went_wrong"](), fields: {} },
        });
      }
    },
  });

  return (
    <LayoutAuth showVisuals={false}>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{m["auth.login_2fa.title"]()}</CardTitle>
          <CardDescription>
            {useRecovery
              ? m["auth.login_2fa.recovery_description"]()
              : m["auth.login_2fa.description"]()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {useRecovery ? (
            <recoveryForm.AppForm>
              <recoveryForm.FormRoot className="flex flex-col gap-4">
                <recoveryForm.AppField
                  name="recoveryCode"
                  children={(field) => <field.TextField label={m["auth.recovery_code.label"]()} />}
                />
                <recoveryForm.SubmitButton className="w-full">
                  {m["common.actions.verify"]()}
                </recoveryForm.SubmitButton>
              </recoveryForm.FormRoot>
            </recoveryForm.AppForm>
          ) : (
            <codeForm.AppForm>
              <codeForm.FormRoot className="flex flex-col gap-4">
                <codeForm.AppField
                  name="code"
                  children={(field) => <field.TextField label={m["auth.login_2fa.code_label"]()} />}
                />
                <codeForm.SubmitButton className="w-full">
                  {m["common.actions.verify"]()}
                </codeForm.SubmitButton>
              </codeForm.FormRoot>
            </codeForm.AppForm>
          )}

          <button
            type="button"
            className="mt-4 text-sm text-blue-600 hover:underline dark:text-blue-400"
            onClick={() => {
              setUseRecovery((v) => !v);
            }}
          >
            {useRecovery
              ? m["auth.login_2fa.use_authenticator_code"]()
              : m["auth.login_2fa.use_recovery_code"]()}
          </button>
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
